// ============================================================================
// ORDERS · Collection — the season's cards, one read (C4)
// ============================================================================
// One query for the whole world: collection_cards(p_season_year) answers the
// envelope { ok, season, synced_at, counts, cards[] } and every surface — the
// entry tile, the header pills, the Wall, the Table — reads the same cache
// entry. Keys sit under ['collection', …] (the Desk's ['orders','desk'] shape);
// 60 s stale, refetch on focus so a synchroniser run shows up when the person
// comes back to the tab. A refusal (42501) and a PGRST202 (package not run)
// are not transient: retried never; everything else once (the Desk's rule).
// C8 · one entry per line: ['collection','cards', year, 'sun' | 'optic']. The
// line is the store's (useCollectionLine) unless the caller names one.
// ============================================================================
import { useQuery } from '@tanstack/react-query';
import { fetchCollectionCards, cardsQueryKey, COLLECTION_SEASON_YEAR } from './api';
import { isMissingFn } from '../desk/useDeskData';
import { useCollectionLine } from './useCollectionLine';
import { LIVE_POLL_MS } from './collectionLive';

export const COLLECTION_KEY = ['collection'];
export const cardsKey = cardsQueryKey;

const isRefusal = (e) => e?.code === '42501' || /42501/.test(String(e?.message || ''));

/** Read state, decided once (Conv #185): failed › loading › ready. */
export function useCollectionCards(seasonYear = COLLECTION_SEASON_YEAR, { enabled = true, line: forLine, poll = false } = {}) {
  const { line: storeLine } = useCollectionLine();
  const line = forLine || storeLine;
  const q = useQuery({
    queryKey: cardsKey(seasonYear, line),
    enabled,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: poll ? LIVE_POLL_MS : false,   // CLB-010 · the world polls (collectionLive.js); the entry tile does not
    refetchIntervalInBackground: false,
    retry: (n, e) => !isMissingFn(e) && !isRefusal(e) && n < 1,
    queryFn: () => fetchCollectionCards(seasonYear, line),
  });
  const env = q.data || null;
  return {
    env,
    line,
    cards: env?.cards || [],
    counts: env?.counts || null,
    season: env?.season || null,
    syncedAt: env?.synced_at || null,
    state: q.isError ? 'failed' : q.isPending ? 'loading' : 'ready',
    isPending: q.isPending, isFetching: q.isFetching, isError: q.isError, error: q.error,
    notDeployed: q.isError && isMissingFn(q.error),
    refused: q.isError && isRefusal(q.error),
    refetch: q.refetch,
  };
}
